
import React from "react";
import Button from "rsuite/esm/Button";
import IconButton from "rsuite/esm/IconButton";
import Dropdown from "rsuite/esm/Dropdown";
import Whisper from "rsuite/esm/Whisper";
import Tooltip from "rsuite/esm/Tooltip";
import Modal from "rsuite/esm/Modal";
import Input from "rsuite/esm/Input";
import SelectPicker from "rsuite/esm/SelectPicker";
import Loader from "rsuite/esm/Loader";
import { useState, useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { HiDotsVertical } from "react-icons/hi";
import { getCookie } from "../../../common/Utility";
import toast from "react-hot-toast";

const BulkEdit = ({
  selectedLeads = [],
  onCancel,
  columnsDef,
}) => {
  if (!selectedLeads || selectedLeads.length === 0) {
    return null;
  }

  const [showModal, setShowModal] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [fieldValue, setFieldValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  let user_uuid = getCookie("muvi_useruuid");

  const handleCancel = () => {
    if (typeof onCancel === "function") {
      onCancel();
    }
  };

  const fetchOptions = async (action) => {
    if (action.attribute_type === "api") {
      setLoading(true);
      try {
        // As per user instructions, make a GET request.
        // Assuming action has an api_url or similar property.
        // If not, I'll use a placeholder for now as requested by user's example response.
        const url =
          action.api_url || `${process.env.REACT_APP_CRM}/api/get-options`; // Placeholder if not found
        const response = await fetch(url, { method: "GET" });
        const data = await response.json();
        // The user provided this example response:
        // [{"id":"aafef3aff9ee4003aa4167948992f7f0","value":" Subhendu Baral"}, ...]
        if (Array.isArray(data)) {
          setOptions(
            data.map((item) => ({ label: item.value, value: item.id }))
          );
        } else if (data && data.data && Array.isArray(data.data)) {
          // Alternative common structure
          setOptions(
            data.data.map((item) => ({ label: item.value, value: item.id }))
          );
        }
      } catch (error) {
        console.error("Error fetching options:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleActionClick = (action) => {
    setCurrentAction(action);
    setFieldValue("");
    setShowModal(true);
    if (action.attribute_type === "api") {
      fetchOptions(action);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentAction(null);
    setFieldValue("");
    setOptions([]);
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        mautic_ids: selectedLeads,
        update_fields: {
          [currentAction.field]: fieldValue,
        },
        muvi_useruuid: user_uuid,
      };
      const response = await fetch(`${process.env.REACT_APP_CRM}/crm/contacts/update/bulk`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        const result = await response.json();
        onCancel();
        toast.success(result.message || "Bulk update successful");
      }
    } catch (error) {
      console.error("Error during bulk update:", error);
    }
  };

  const updateActions =
    (columnsDef && columnsDef.filter((col) => col.bulkupdate === true)) || [];

  return (
    <div
      className="bulk-edit-container"
      style={{
        padding: "10px 20px",
        borderRadius: "8px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
        border: "1px solid #edf2f7",
        animation: "slideDown 0.3s ease-out",
        background: "linear-gradient(to right, #ffffff, #f8fafc)",
        // boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      }}
    >
      <style>
        {`
                @keyframes slideDown {
                    from { transform: translateY(-10px); opacity: 0; }
                    to { transform: translateY(0); opacity: 1; }
                }
                `}
      </style>

      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div
          style={{
            color: "#000",
            padding: "4px 12px",
            fontSize: "14px",
            // fontWeight: "600",
          }}
        >
          {selectedLeads.length} {selectedLeads.length === 1 ? "Item" : "Items"}{" "}
          selected
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
        {updateActions.length > 0 && (
          <Dropdown
            renderToggle={(props, ref) => (
              <Whisper
                placement="top"
                trigger="hover"
                speaker={<Tooltip>Edit</Tooltip>}
              >
                <Button
                  {...props}
                  ref={ref}
                  appearance="link"
                  size="sm"
                  style={{
                    color: "#475569",
                    padding: 0,
                    fontWeight: 500
                  }}
                >
                  Edit
                </Button>
              </Whisper>

            )}
            placement="bottomEnd"
          >
            {updateActions.map((action, index) => (
              <Dropdown.Item
                key={index}
                onClick={() => handleActionClick(action)}
              >
                {action.headerName}
              </Dropdown.Item>
            ))}
          </Dropdown>
        )}
        <Button
          appearance="subtle"
          onClick={handleCancel}
          style={{
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <IoClose size={18} />
          Cancel
        </Button>
      </div>
      <Modal
        open={showModal}
        onClose={handleCloseModal}
        size="xs"
        backdrop="static"
      >
        <Modal.Header>
          <Modal.Title>
            Bulk Edit: {currentAction && currentAction.headerName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ marginBottom: "15px" }}>
            <label
              style={{ display: "block", marginBottom: "8px", fontWeight: 500 }}
            >
              {currentAction && currentAction.headerName}
            </label>
            {currentAction && currentAction.attribute_type === "api" ? (
              <SelectPicker
                data={options}
                value={fieldValue}
                onChange={setFieldValue}
                block
                searchable
                placeholder={`Select ${
                  currentAction && currentAction.headerName
                }`}
                loading={loading}
                renderMenu={(menu) => {
                  if (loading) {
                    return (
                      <div style={{ padding: "20px", textAlign: "center" }}>
                        <Loader content="Loading..." />
                      </div>
                    );
                  }
                  return menu;
                }}
              />
            ) : (
              <Input
                placeholder={`Enter ${
                  currentAction && currentAction.headerName
                }`}
                value={fieldValue}
                onChange={setFieldValue}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSubmit} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleCloseModal} appearance="subtle">
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BulkEdit;



// WEBPACK FOOTER //
// ./src/components/crm/leads/components/BulkEdit.js